import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static targets = [
    "advancedOptions",
    "advancedToggleIcon",
    "variableFeeScheduleToggle",
    "variableFeeScheduleSection",
    "tieredCarryToggle",
    "tieredCarrySection",
    "feeSchedulesList",
    "feeScheduleRow",
    "performanceTiersList",
    "feeScheduleChart",
    "performanceTierChart",
    "averageFeeContainer",
    "averageFeeValue",
    "averageFeeStatus"
  ]

  connect() {
    this.feeScheduleChart = null
    this.performanceTierChart = null

    // Initialize charts if advanced section is visible
    if (this.hasAdvancedOptionsTarget && !this.advancedOptionsTarget.classList.contains('hidden')) {
      this.initializeCharts()
    }
  }

  disconnect() {
    // Clean up charts when controller disconnects
    if (this.feeScheduleChart) {
      this.feeScheduleChart.destroy()
    }
    if (this.performanceTierChart) {
      this.performanceTierChart.destroy()
    }
  }

  toggleAdvancedOptions() {
    this.advancedOptionsTarget.classList.toggle('hidden')

    // Rotate the chevron icon
    if (this.hasAdvancedToggleIconTarget) {
      this.advancedToggleIconTarget.classList.toggle('rotate-180')
    }

    // Initialize charts when opening advanced options
    if (!this.advancedOptionsTarget.classList.contains('hidden')) {
      setTimeout(() => this.initializeCharts(), 100)
    }
  }

  toggleVariableFeeSchedule() {
    const isChecked = this.variableFeeScheduleToggleTarget.checked
    this.variableFeeScheduleSectionTarget.classList.toggle('hidden', !isChecked)

    if (isChecked && this.hasFeeScheduleChartTarget) {
      setTimeout(() => this.updateFeeScheduleChart(), 100)
    }
  }

  toggleTieredCarry() {
    const isChecked = this.tieredCarryToggleTarget.checked
    this.tieredCarrySectionTarget.classList.toggle('hidden', !isChecked)

    if (isChecked && this.hasPerformanceTierChartTarget) {
      setTimeout(() => this.updatePerformanceTierChart(), 100)
    }
  }

  addFeeScheduleRow() {
    const tbody = this.feeSchedulesListTarget
    const newIndex = new Date().getTime()

    // Calculate the next sequential year
    const existingYears = Array.from(tbody.querySelectorAll('input[name*="[year]"]'))
      .map(input => parseInt(input.value))
      .filter(year => !isNaN(year))

    const nextYear = existingYears.length > 0 ? Math.max(...existingYears) + 1 : 1

    const row = document.createElement('tr')
    row.className = 'border-b dark:border-gray-700'
    row.setAttribute('data-fund-contracts-form-target', 'feeScheduleRow')

    row.innerHTML = `
      <td class="px-4 py-3">
        <input type="number"
               name="fund_contract[management_fee_schedules_attributes][${newIndex}][year]"
               value="${nextYear}"
               readonly
               min="1"
               step="1"
               class="w-20 px-2 py-1 border rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
               data-action="change->fund-contracts-form#updateFeeScheduleChart">
      </td>
      <td class="px-4 py-3">
        <input type="number"
               name="fund_contract[management_fee_schedules_attributes][${newIndex}][fee_percentage]"
               placeholder="e.g., 2.0"
               step="0.1"
               min="0"
               max="10"
               class="w-24 px-2 py-1 border rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white"
               data-action="change->fund-contracts-form#updateFeeScheduleChart">
      </td>
      <td class="px-4 py-3 text-center">
        <button type="button"
                class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                data-action="click->fund-contracts-form#removeFeeScheduleRow">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </button>
      </td>
    `

    tbody.appendChild(row)
    this.updateFeeScheduleChart()
  }

  removeFeeScheduleRow(event) {
    event.target.closest('tr').remove()
    this.updateFeeScheduleChart()
  }

  addPerformanceTierRow() {
    const tbody = this.performanceTiersListTarget
    const newIndex = new Date().getTime()
    const row = document.createElement('tr')
    row.className = 'border-b dark:border-gray-700'

    row.innerHTML = `
      <td class="px-4 py-3">
        <input type="number"
               name="fund_contract[performance_fee_tiers_attributes][${newIndex}][threshold_multiple]"
               placeholder="e.g., 1.5"
               step="0.1"
               min="0"
               class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
               data-action="change->fund-contracts-form#updatePerformanceTierChart">
      </td>
      <td class="px-4 py-3">
        <input type="number"
               name="fund_contract[performance_fee_tiers_attributes][${newIndex}][carry_percentage]"
               placeholder="e.g., 20.0"
               step="0.1"
               min="0"
               max="100"
               class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
               data-action="change->fund-contracts-form#updatePerformanceTierChart">
      </td>
      <td class="px-4 py-3 text-center">
        <button type="button"
                class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                data-action="click->fund-contracts-form#removePerformanceTierRow">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </button>
      </td>
    `

    tbody.appendChild(row)
    this.updatePerformanceTierChart()
  }

  removePerformanceTierRow(event) {
    event.target.closest('tr').remove()
    this.updatePerformanceTierChart()
  }

  initializeCharts() {
    if (this.hasFeeScheduleChartTarget && this.variableFeeScheduleToggleTarget.checked) {
      this.updateFeeScheduleChart()
    }

    if (this.hasPerformanceTierChartTarget && this.tieredCarryToggleTarget.checked) {
      this.updatePerformanceTierChart()
    }
  }

  updateChartsFromTemplate() {
    // Update both charts when template selection changes
    // This ensures the performance tier chart reflects the new base carry percentage
    if (this.hasFeeScheduleChartTarget && this.hasVariableFeeScheduleToggleTarget && this.variableFeeScheduleToggleTarget.checked) {
      this.updateFeeScheduleChart()
    }

    if (this.hasPerformanceTierChartTarget && this.hasTieredCarryToggleTarget && this.tieredCarryToggleTarget.checked) {
      this.updatePerformanceTierChart()
    }
  }

  updateFeeScheduleChart() {
    if (!this.hasFeeScheduleChartTarget) return

    // Get base management fee from the form (from templates or custom input)
    const baseManagementFeeInput = document.querySelector('input[name="fund_contract[management_fee_percentage]"]')
    let baseManagementFee = 2.0 // Default to 2% if not found

    if (baseManagementFeeInput && baseManagementFeeInput.value) {
      baseManagementFee = parseFloat(baseManagementFeeInput.value)
    } else {
      // Check which template is selected
      const selectedTemplate = document.querySelector('input[name="template"]:checked')
      if (selectedTemplate) {
        if (selectedTemplate.value === '2_and_20') {
          baseManagementFee = 2.0
        } else if (selectedTemplate.value === '1_5_and_15') {
          baseManagementFee = 1.5
        }
      }
    }

    // Collect data from form
    const rows = this.feeSchedulesListTarget.querySelectorAll('tr')
    const data = []

    rows.forEach(row => {
      const yearInput = row.querySelector('input[name*="[year]"]')
      const feeInput = row.querySelector('input[name*="[fee_percentage]"]')

      if (yearInput && feeInput && yearInput.value && feeInput.value) {
        data.push({
          year: parseInt(yearInput.value),
          fee: parseFloat(feeInput.value)
        })
      }
    })

    // Update average fee calculation
    this.updateAverageFee(data, baseManagementFee)

    // Sort by year
    data.sort((a, b) => a.year - b.year)

    // Detect dark mode
    const isDarkMode = document.documentElement.classList.contains('dark')
    const textColor = isDarkMode ? '#D1D5DB' : '#374151'
    const gridColor = isDarkMode ? '#374151' : '#E5E7EB'

    // Prepare chart data - if no data, show empty chart with just the baseline
    const chartSeries = data.length > 0 ? data.map(d => d.fee) : []
    const chartCategories = data.length > 0 ? data.map(d => `Year ${d.year}`) : []
    const maxValue = data.length > 0 ? Math.max(...data.map(d => d.fee), baseManagementFee + 2) : baseManagementFee + 2

    const chartData = {
      series: [{
        name: 'Management Fee',
        data: chartSeries
      }],
      chart: {
        type: 'line',
        height: 250,
        toolbar: {
          show: false
        },
        background: 'transparent',
        zoom: {
          enabled: false
        }
      },
      stroke: {
        curve: 'straight',
        width: 3
      },
      markers: {
        size: 5
      },
      xaxis: {
        categories: chartCategories,
        title: {
          text: 'Fund Year',
          style: {
            color: textColor
          }
        },
        labels: {
          style: {
            colors: textColor
          }
        }
      },
      yaxis: {
        title: {
          text: 'Fee Percentage (%)',
          style: {
            color: textColor
          }
        },
        labels: {
          style: {
            colors: textColor
          }
        },
        min: 0,
        max: maxValue
      },
      grid: {
        borderColor: gridColor
      },
      colors: ['#1C64F2'],
      tooltip: {
        theme: isDarkMode ? 'dark' : 'light',
        y: {
          formatter: (val) => `${val}%`
        }
      },
      annotations: {
        yaxis: [{
          y: baseManagementFee,
          borderColor: isDarkMode ? '#6B7280' : '#9CA3AF',
          strokeDashArray: 4,
          label: {
            text: `Base: ${baseManagementFee}%`,
            style: {
              color: '#FFFFFF',
              background: isDarkMode ? '#374151' : '#6B7280'
            }
          }
        }]
      }
    }

    if (this.feeScheduleChart) {
      this.feeScheduleChart.updateOptions(chartData)
    } else {
      this.feeScheduleChart = new ApexCharts(this.feeScheduleChartTarget, chartData)
      this.feeScheduleChart.render()
    }
  }

  updatePerformanceTierChart() {
    if (!this.hasPerformanceTierChartTarget) return

    // Get base performance fee from the form (from templates or custom input)
    const basePerformanceFeeInput = document.querySelector('input[name="fund_contract[performance_fee_percentage]"]')
    let baseCarry = 20.0 // Default to 20% if not found

    if (basePerformanceFeeInput && basePerformanceFeeInput.value) {
      baseCarry = parseFloat(basePerformanceFeeInput.value)
    } else {
      // Check which template is selected
      const selectedTemplate = document.querySelector('input[name="template"]:checked')
      if (selectedTemplate) {
        if (selectedTemplate.value === '2_and_20') {
          baseCarry = 20.0
        } else if (selectedTemplate.value === '1_5_and_15') {
          baseCarry = 15.0
        }
      }
    }

    // Collect tier data from form
    const rows = this.performanceTiersListTarget.querySelectorAll('tr')
    const tiers = []

    rows.forEach(row => {
      const thresholdInput = row.querySelector('input[name*="[threshold_multiple]"]')
      const carryInput = row.querySelector('input[name*="[carry_percentage]"]')

      if (thresholdInput && carryInput && thresholdInput.value && carryInput.value) {
        tiers.push({
          threshold: parseFloat(thresholdInput.value),
          carry: parseFloat(carryInput.value)
        })
      }
    })

    // Sort by threshold
    tiers.sort((a, b) => a.threshold - b.threshold)

    // Build complete data series with intermediate points to create step effect
    // Start at 1x with base carry
    const data = [{ x: 1, y: baseCarry }]

    // Add each tier with a point just before (horizontal line) and at the threshold (vertical jump)
    let currentCarry = baseCarry
    tiers.forEach((tier) => {
      // Add point just before the threshold at current carry level (horizontal line)
      data.push({ x: tier.threshold - 0.01, y: currentCarry })
      // Add point at the threshold with new carry level (vertical jump)
      data.push({ x: tier.threshold, y: tier.carry })
      currentCarry = tier.carry
    })

    // Add final point to extend the line
    const maxThreshold = tiers.length > 0 ? Math.max(...tiers.map(t => t.threshold)) : 5
    data.push({ x: Math.max(maxThreshold + 2, 10), y: currentCarry })

    // Detect dark mode
    const isDarkMode = document.documentElement.classList.contains('dark')
    const textColor = isDarkMode ? '#D1D5DB' : '#374151'
    const gridColor = isDarkMode ? '#374151' : '#E5E7EB'

    const chartData = {
      series: [{
        name: 'Carry Percentage',
        data: data
      }],
      chart: {
        type: 'line',
        height: 250,
        toolbar: {
          show: false
        },
        background: 'transparent',
        zoom: {
          enabled: false
        },
        events: {
          mounted: (chart) => {
            this.customizeTickSizes(chart)
          },
          updated: (chart) => {
            this.customizeTickSizes(chart)
          }
        }
      },
      stroke: {
        curve: 'straight',
        width: 4,
        lineCap: 'square'
      },
      markers: {
        size: 6,
        strokeWidth: 2,
        strokeColors: '#FFFFFF',
        hover: {
          size: 8,
          strokeWidth: 2
        }
      },
      xaxis: {
        type: 'numeric',
        min: 1,
        max: Math.max(...data.map(d => d.x), 10),
        tickAmount: (Math.max(...data.map(d => d.x), 10) - 1) * 10, // Create ticks at 0.1 intervals
        axisTicks: {
          show: true,
          color: gridColor,
          height: 6
        },
        axisBorder: {
          show: true,
          color: gridColor
        },
        title: {
          text: 'Return Multiple',
          style: {
            color: textColor
          }
        },
        labels: {
          formatter: (val) => {
            // Only show labels for whole numbers
            if (Number.isInteger(val)) {
              return `${val}x`
            }
            return ''
          },
          style: {
            colors: textColor
          },
          offsetY: 10
        }
      },
      yaxis: {
        title: {
          text: 'Carry Percentage (%)',
          style: {
            color: textColor
          }
        },
        labels: {
          style: {
            colors: textColor
          }
        },
        min: 0,
        max: Math.max(...data.map(d => d.y), baseCarry + 10)
      },
      grid: {
        borderColor: gridColor
      },
      colors: ['#16BDCA'],
      tooltip: {
        theme: isDarkMode ? 'dark' : 'light',
        y: {
          formatter: (val) => `${val}%`
        }
      },
      annotations: {
        yaxis: [{
          y: baseCarry,
          borderColor: isDarkMode ? '#6B7280' : '#9CA3AF',
          strokeDashArray: 4,
          label: {
            text: `Base: ${baseCarry}%`,
            style: {
              color: '#FFFFFF',
              background: isDarkMode ? '#374151' : '#6B7280'
            }
          }
        }]
      }
    }

    if (this.performanceTierChart) {
      this.performanceTierChart.updateOptions(chartData)
    } else {
      this.performanceTierChart = new ApexCharts(this.performanceTierChartTarget, chartData)
      this.performanceTierChart.render()
    }
  }

  updateAverageFee(data, baseFee) {
    if (!this.hasAverageFeeContainerTarget) return

    if (data.length === 0) {
      // No data - show placeholder
      this.averageFeeValueTarget.textContent = '-'
      this.averageFeeStatusTarget.textContent = 'Add years to calculate'
      this.averageFeeStatusTarget.className = 'text-xs mt-1 text-gray-500 dark:text-gray-400'
      this.averageFeeContainerTarget.className = 'mt-4 p-4 bg-white dark:bg-gray-700 rounded-lg border-2 border-gray-300 dark:border-gray-600'
      return
    }

    // Calculate average
    const totalFee = data.reduce((sum, item) => sum + item.fee, 0)
    const averageFee = totalFee / data.length
    const difference = averageFee - baseFee
    const percentDiff = ((difference / baseFee) * 100).toFixed(1)

    // Update value display
    this.averageFeeValueTarget.textContent = `${averageFee.toFixed(2)}%`

    // Determine status and styling based on comparison
    const tolerance = 0.05 // Within 0.05% is considered "perfect"

    if (Math.abs(difference) < tolerance) {
      // Perfect match
      this.averageFeeValueTarget.className = 'text-2xl font-bold text-primary-600 dark:text-primary-400'
      this.averageFeeStatusTarget.textContent = `✓ Matches target (${baseFee}%)`
      this.averageFeeStatusTarget.className = 'text-xs mt-1 text-primary-600 dark:text-primary-400 font-medium'
      this.averageFeeContainerTarget.className = 'mt-4 p-4 bg-white dark:bg-gray-700 rounded-lg border-2 border-primary-500 dark:border-primary-500'
    } else if (difference < 0) {
      // Below target (good for LPs)
      this.averageFeeValueTarget.className = 'text-2xl font-bold text-green-600 dark:text-green-400'
      this.averageFeeStatusTarget.textContent = `↓ ${Math.abs(percentDiff)}% below target (${baseFee}%)`
      this.averageFeeStatusTarget.className = 'text-xs mt-1 text-green-600 dark:text-green-400 font-medium'
      this.averageFeeContainerTarget.className = 'mt-4 p-4 bg-white dark:bg-gray-700 rounded-lg border-2 border-green-500 dark:border-green-500'
    } else {
      // Above target
      this.averageFeeValueTarget.className = 'text-2xl font-bold text-orange-600 dark:text-orange-400'
      this.averageFeeStatusTarget.textContent = `↑ ${percentDiff}% above target (${baseFee}%)`
      this.averageFeeStatusTarget.className = 'text-xs mt-1 text-orange-600 dark:text-orange-400 font-medium'
      this.averageFeeContainerTarget.className = 'mt-4 p-4 bg-white dark:bg-gray-700 rounded-lg border-2 border-orange-500 dark:border-orange-500'
    }
  }

  customizeTickSizes(chart) {
    // Wait a bit for the chart to fully render
    setTimeout(() => {
      const svg = chart.el.querySelector('.apexcharts-xaxis-tick')?.closest('svg')
      if (!svg) return

      // Find all x-axis tick marks
      const ticks = svg.querySelectorAll('.apexcharts-xaxis-tick')

      ticks.forEach((tick) => {
        // Get the x position to determine what value this tick represents
        const x = parseFloat(tick.getAttribute('x1'))

        // Get the chart's x-axis range to calculate the actual value
        const xaxis = chart.w.globals.minX
        const xrange = chart.w.globals.maxX - chart.w.globals.minX
        const chartWidth = chart.w.globals.gridWidth

        // Calculate the approximate value this tick represents
        const value = xaxis + (x / chartWidth) * xrange

        // Determine tick size based on the decimal part
        const decimalPart = Math.abs(value - Math.round(value))
        let tickHeight = 4 // Default small tick for tenths

        if (decimalPart < 0.01) {
          // Whole number (1.0, 2.0, etc.) - largest tick
          tickHeight = 12
        } else if (Math.abs(decimalPart - 0.5) < 0.01) {
          // Half increment (1.5, 2.5, etc.) - medium tick
          tickHeight = 8
        }
        // All other values (0.1, 0.2, 0.3, 0.4, 0.6, 0.7, 0.8, 0.9) get small tick (4px)

        // Update both y1 and y2 to change the tick height
        const y1 = parseFloat(tick.getAttribute('y1'))
        tick.setAttribute('y2', y1 + tickHeight)
      })
    }, 100)
  }
}
